import { useEffect } from 'react';
import { Control, FieldValues, UseFormReset, UseFormSetValue } from 'react-hook-form';

import { Filter, FilterGroup, RegisterType } from 'types';

import { Button } from 'components/Button';
import { CheckboxWithControl } from 'components/CheckboxWithControl';
import { Input } from 'components/Input';
import { SelectWithControl } from 'components/SelectWithControl';

import * as S from './Filters.styles';

const useFilters = (
  filters: FilterGroup[],
  register: RegisterType,
  onSubmit: () => void,
  reset: UseFormReset<FieldValues>,
  setValue: UseFormSetValue<FieldValues>,
  isLoading: boolean,
  defaultValues?: URLSearchParams,
  control?: Control
) => {
  const filterValues = filters
    ?.map((item: FilterGroup) => item.filters.map((filter: Filter) => filter.name))
    .flat()
    .map((item: string) => ({
      [item]: '',
    }))
    .reduce((result, currentObject) => {
      for (const key in currentObject) {
        if (currentObject.hasOwnProperty(key)) {
          result[key] = currentObject[key];
        }
      }
      return result;
    });
  const queryString = window.location.search;

  const handleClearFilter = () => {
    reset(filterValues);
    if (queryString) {
      onSubmit();
    }
  };

  const filtersIntoComponents = filters?.map((item: FilterGroup) => (
    <S.FilterContainer
      onKeyDown={(event) => event.key === 'Enter' && onSubmit()}
      isCentered={item.isCentered}
      key={item.id}
    >
      {item.filters.map((filter: Filter) => {
        if (filter.type === 'select' && filter.options && control) {
          return (
            <S.Filter key={filter.name} flexBasis={filter.flexBasis} flex={filter.flex}>
              <SelectWithControl
                control={control}
                name={filter.name}
                scale="xs"
                label={filter.label}
                options={filter.options}
                disabled={filter.disabled}
                motive="filter"
                selectMotive="filter"
              />
            </S.Filter>
          );
        }

        if (filter.type === 'checkbox' && control) {
          return (
            <S.Filter key={filter.name} flexBasis={filter.flexBasis} flex={filter.flex}>
              <CheckboxWithControl
                control={control}
                disabled={filter.disabled}
                name={filter.name}
                label={filter.label}
                isCheckIcon
              />
            </S.Filter>
          );
        }

        if (filter.type === 'date') {
          return (
            <S.Filter key={filter.name} flexBasis={filter.flexBasis} flex={filter.flex}>
              <Input
                key={filter.name}
                register={register}
                name={filter.name}
                label={filter.label}
                motive="m4"
                type="date"
                disabled={filter.disabled}
              />
            </S.Filter>
          );
        }

        return (
          <S.Filter key={filter.name} flexBasis={filter.flexBasis} flex={filter.flex}>
            <Input
              motive="search"
              name={filter.name}
              label={filter.label}
              register={register}
              disabled={filter.disabled}
              placeholder={filter.placeholder}
            />
          </S.Filter>
        );
      })}

      {item.id === filters.length ? (
        <S.Buttons>
          <Button
            fontFamily="LFT Zeno Condensed"
            onClick={onSubmit}
            scale="lg"
            motive="filter"
            textTransform="uppercase"
            disabled={isLoading}
          >
            Search
          </Button>

          <Button
            onClick={handleClearFilter}
            type="button"
            iconLeft="FiXCircle"
            scale="lg"
            motive="filterSecondary"
            fontFamily="LFT Zeno Condensed"
            textTransform="uppercase"
            disabled={isLoading}
          >
            Clear filters
          </Button>
        </S.Buttons>
      ) : null}
    </S.FilterContainer>
  ));

  useEffect(() => {
    if (defaultValues) {
      for (const entry of Array.from(defaultValues.entries())) {
        const [param, value] = entry;
        const convertedValue = value === 'false' ? false : value;
        setValue(param, convertedValue);
      }
    }
  }, [defaultValues]);

  return {
    filtersIntoComponents,
  };
};

export { useFilters };
